import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {CodeAndExchange, Securities} from "../../instruments/types/CodeAndExchange";
import {DateTimeUtil} from "../../js/util/DateTimeUtil";
import {ClientEventService} from "../../services/client-events/ClientEventService";
import {SystemEventService} from "../../services/system-state/SystemEventService";
import {ClientEventType} from "../../services/client-events/models/ClientEventType";
import {SystemEvent} from "../../services/system-state/SystemEvent";
import {IsoDate} from "../../pseudo-types/DateTypes";
import {ArrayUtil} from "../../js/util/ArrayUtil";
import {SecurityDividendsRecord} from "../types/DividendsRecord";
import {DividendsService} from "../service/DividendsService";
import {ObjectUtil} from "../../js/util/ObjectUtil";
import {DividendsModel} from "../models/DividendsModel";
import {SecurityMap, SecurityMapOf} from "../../security/common/map/SecurityMap";
import {isNotNull} from "../../js/util/UtilFunctions";

export class DividendsStore {

    public static readonly id = "dividendsStore";

    private readonly dividends$ = new ReplaySubject<SecurityMap<DividendsModel>>(1);
    private readonly reload$ = new BehaviorSubject(true);
    private readonly lastReload$ = new BehaviorSubject(DateTimeUtil.isoToday());

    /** @ngInject */
    public constructor(private readonly dividendsService: DividendsService,
                       private readonly clientEventService: ClientEventService,
                       private readonly systemEventService: SystemEventService) {
        this.init();
    }

    private init() {
        this.watchReload();
        this.watchDividendUpdates();
        this.watchIdleEnd();
    }

    private watchReload() {
        this.reload$
            .switchMap(() => this.retrieveDividends())
            .map((dividends) => this.buildDividendMap(dividends))
            .do(() => this.lastReload$.next(DateTimeUtil.isoToday()))
            .subscribe((map) => this.dividends$.next(map));
    }

    private retrieveDividends(): Observable<SecurityDividendsRecord> {
        return Observable.of(true)
                         .flatMap(() => this.dividendsService.latest())
                         .catch(() => Observable.of([]));
    }

    private buildDividendMap(record: SecurityDividendsRecord): SecurityMap<DividendsModel> {
        const map = SecurityMapOf<DividendsModel>();

        const today = DateTimeUtil.isoToday();

        record.forEach((securityDividends) => {
            const key = ObjectUtil.pick(securityDividends, "code", "exchange");
            const dividends = securityDividends.dividends;
            const model = DividendsModel.of(today, securityDividends.code, securityDividends.exchange, dividends);
            map.set(key, model);
        });

        return map;
    }

    private watchDividendUpdates() {
        this.clientEventService
            .events()
            .filter((evt) => evt.type === ClientEventType.DIVIDENDS_UPDATED)
            .subscribe(() => this.reload$.next(true));
    }

    /* When we end idle we may have missed an update event due to overnight, so we reload manually if so */
    private watchIdleEnd() {
        this.systemEventService
            .idle()
            .filter((evt) => evt === SystemEvent.IDLE_END)
            .withLatestFrom(this.lastReload$)
            .filter(([_, lastReload]) => this.isNewDay(lastReload))
            .subscribe(() => this.reload$.next(true));
    }

    private isNewDay(lastReload: IsoDate): boolean {
        const now = DateTimeUtil.isoToday();
        return now > lastReload;
    }

    public stream(security: CodeAndExchange | Securities): Observable<DividendsModel> {
        if (ArrayUtil.isArray(security)) {
            const sources = security.map((s) => this.stream(s));
            return Observable.merge(...sources);
        } else {
            return this.source(security);
        }
    }

    private source(security: CodeAndExchange): Observable<DividendsModel> {
        return this.dividends$
                   .map((d) => d.get(security))
                   .filter(isNotNull);
    }
}
